import { IDisposable } from "Global/Base/IDisposable";
import { Key } from "Global/Base/Utils/EventUtils";
import { EventsController } from "Global/Dom/EventsController";
import { Edit } from "UI/UI.BaseControls/Edit";
import { GenericControl } from "../UI.BaseControls/GenericControl";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { ClassListGridRow } from "./DataGridSubcontrols/Components/ClassListGridRow";
import { EmptyTreeDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/EmptyTreeDataGridMessage";
import { IEmptyDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/IEmptyDataGridMessage";
import { ClassListDataSourceLightDataSource } from "./DataSource/ClassListDataSourceLightDataSource";

const className = "class-list-grid";

/**
 * Třída zobrazí seznam všech tříd
 */
export class ClassListGrid extends HTMLElement implements IDisposable {
    public emptyMessage: IEmptyDataGridMessage;
    public dataSource: ClassListDataSourceLightDataSource = new ClassListDataSourceLightDataSource();
    private _dataTable: HTMLUListElement;
    private _searchInput: Edit = new Edit();
    private _body: HTMLElement;
    private _toolbar: HTMLElement;
    private _lastSearch: string = "";
    private _debounced: number;

    private _eventController: EventsController = new EventsController();

    private _isDisposed = false;

    public get dataRows(): ClassListGridRow[] {
        return Array.from(this._dataTable.children) as ClassListGridRow[];
    }

    public get selectedClassNames(): string[] {
        return this.dataRows.filter(item => item.isChecked).map(item => item.value);
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);
        this.classList.add("dataGrid");

        this._createToolbar();
        this._body = createElement({ classes: `${className}__body` });

        this._body.classList.add(className, "dataGrid");
        this._body.tabIndex = -1;

        const listDiv = createElement({ classes: "p-list-div" });

        this._dataTable = createElement({ classes: "list-div" }) as HTMLUListElement;

        listDiv.appendChild(this._dataTable);
        this._body.appendChild(listDiv);

        this.emptyMessage = new EmptyTreeDataGridMessage();
        this._body.appendChild(this.emptyMessage);

        this.appendChild(this._toolbar);
        this.appendChild(this._body);

        this._registerEventListeners();
    }

    private _filterClass(): void {
        const name = this._searchInput.value.trim().toLowerCase();
        if (this._lastSearch === name) return;

        this._lastSearch = name;
        if (name.length === 0) {
            this.dataRows.forEach((item, index) => {
                item.style.display = "";
                if (index === 0) {
                    item.tabIndex = 0;
                } else {
                    item.tabIndex = -1;
                }
            });
        } else {
            let firstMatchedRow = false;
            this.dataRows.forEach(item => {
                if (item.value.toLowerCase().includes(name)) {
                    item.style.display = "";
                    if (!firstMatchedRow) {
                        item.tabIndex = 0;
                        firstMatchedRow = true;
                    }
                } else {
                    item.style.display = "none";
                    item.tabIndex = -1;
                }
            });
        }
    }

    private _createSkeletons(count: number = 20): void {
        const getRandomInt = (min: number, max: number) => {
            min = Math.ceil(min);
            max = Math.floor(max);
            return Math.floor(Math.random() * (max - min + 1)) + min;
        };

        if (count === 0) {
            if (this.querySelector(".skeleton-row") !== null) this._dataTable.innerHTML = "";
        } else {
            let str = "";
            for (let i = 0; i < count; i++) {
                str += this._createSkeleton(getRandomInt(50, 130));
            }

            this._dataTable.innerHTML = str;
        }
    }

    private _createSkeleton(width: number): string {
        return `<div class="skeleton-row DataRow"><div class="check-skeleton skeleton-component"></div><div class="text-skeleton skeleton-component" style="width: ${width}px;"></div></div>`;
    }

    private _registerEventListeners(): void {
        this._eventController.addListener(this, "keydown", (event) => {
            const siblingType = event.key === Key.ArrowDown ? "nextElementSibling" : "previousElementSibling";

            const focusedElement = document.activeElement;
            if (this.contains(focusedElement) && focusedElement.classList.contains("class-list-row")) {
                const row = focusedElement as ClassListGridRow;

                switch (event.key) {
                    case Key.Space:
                        event.preventDefault();
                        row.isChecked = !row.isChecked;
                        break;
                    case Key.ArrowDown:
                    case Key.ArrowUp: {
                        event.preventDefault();
                        let sibling = row[siblingType] as HTMLElement;

                        if (sibling) {
                            if (sibling.style.display === "") {
                                sibling.focus();
                                break;
                            } else {
                                while (sibling && sibling.style.display === "none") {
                                    sibling = sibling[siblingType] as HTMLElement;
                                }

                                sibling?.focus();
                            }
                        }
                        break;
                    }
                    default:
                        break;
                }
            }
        });
    }

    private _createToolbar(): void {
        this._toolbar = createElement({ classes: `${className}__toolbar` });
        this._toolbar.appendChild(this._searchInput);

        this._searchInput.setAttribute("data-captionlockey", "GUI-781367"); // Vyhledávání

        this._eventController.addListener(this._searchInput, "keyup", (e: KeyboardEvent) => {
            if (e.key === Key.Enter) {
                clearTimeout(this._debounced);
                this._filterClass();
            } else if (e.key === Key.Escape) {
                this._searchInput.value = "";
                clearTimeout(this._debounced);
                this._filterClass();
            } else {
                clearTimeout(this._debounced);
                this._debounced = self.setTimeout(() => this._filterClass(), 300);
            }
        });
    }

    public async loadClasses(): Promise<void> {
        this._createSkeletons();
        const classes = await this.dataSource.loadClassList();
        this._createSkeletons(0);

        for (const [index, className] of classes.entries()) {
            const row = new ClassListGridRow();
            row.value = className;

            this._dataTable.appendChild(row.render());

            if (index === 0) {
                row.tabIndex = index;
            } else {
                row.tabIndex = -1;
            }

            this._eventController.addListener(row, "change", () => {
                GenericControl.callEvent(this, "change");
            });
        }
    }

    public dispose(): void {
        if (this._isDisposed) {
            return;
        }

        this._eventController.removeAllListeners();

        for (const row of this.dataRows) {
            row.dispose();
        }

        this._isDisposed = true;
    }
}

registerCustomElement("tsc-ClassListGrid", ClassListGrid);
